import {
  Camera,
  MessageSquareQuote,
  Leaf,
  SlidersHorizontal,
  Boxes,
  ChevronDown,
  FolderTree,
  Gauge,
  Images,
  LogOut,
  Menu,
  PackageSearch,
  PackageOpen,
  Settings,
  Users,
  Video,
  X,
  LayoutTemplate
} from "lucide-react";
import { useState } from "react";
import {
  NavLink,
  Outlet,
  useNavigate
} from "react-router";
import {
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import {
  getCurrentAdmin,
  logoutAdmin
} from "../services/auth";

const navigation = [
  {
    label: "Dashboard",
    path: "/admin",
    icon: Gauge,
    end: true
  },
  {
    label: "Homepage",
    path: "/admin/homepage",
    icon: LayoutTemplate
  },
  {
    label: "Homepage Sections",
    path: "/admin/homepage/sections",
    icon: SlidersHorizontal
  },
  {
    label: "Products",
    path: "/admin/products",
    icon: Boxes
  },
  {
    label: "Inventory",
    path: "/admin/inventory",
    icon: PackageOpen
  },
  {
    label: "Categories",
    path: "/admin/categories",
    icon: FolderTree
  },
  {
    label: "Ingredients",
    path: "/admin/ingredients",
    icon: Leaf
  },
  {
    label: "Testimonials",
    path: "/admin/testimonials",
    icon: MessageSquareQuote
  },
  {
    label: "Instagram Feed",
    path: "/admin/instagram",
    icon: Camera
  },
  {
    label: "Gift Hampers",
    path: "/admin/gift-hampers",
    icon: PackageSearch
  },
  {
    label: "Media Library",
    path: "/admin/media",
    icon: Images
  },
  {
    label: "Making RiNi",
    path: "/admin/videos",
    icon: Video
  },
  {
    label: "Orders",
    path: "/admin/orders",
    icon: PackageSearch
  },
  {
    label: "Customers",
    path: "/admin/customers",
    icon: Users
  },
  {
    label: "Settings",
    path: "/admin/settings",
    icon: Settings
  }
];

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] =
    useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const adminQuery = useQuery({
    queryKey: ["admin", "me"],
    queryFn: getCurrentAdmin,
    staleTime: 5 * 60 * 1000
  });

  async function handleLogout() {
    await logoutAdmin();

    queryClient.removeQueries({
      queryKey: ["admin"]
    });

    navigate("/admin/login", {
      replace: true
    });
  }

  const sidebar = (
    <div className="flex h-full flex-col bg-[#30231f] text-white">
      <div className="border-b border-white/10 px-6 py-6">
        <span className="block font-serif text-3xl font-semibold tracking-[0.08em]">
          RiNi
        </span>

        <span className="text-[9px] tracking-[0.34em] text-[#b8c7aa]">
          ADMIN WORKSPACE
        </span>
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6">
        {navigation.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={() =>
                setMobileOpen(false)
              }
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition",
                  isActive
                    ? "bg-white text-[#38251e]"
                    : "text-white/65 hover:bg-white/10 hover:text-white"
                ].join(" ")
              }
            >
              <Icon size={19} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-4">
        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-white/65 transition hover:bg-white/10 hover:text-white"
        >
          <LogOut size={19} />
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f5f3ef]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 lg:block">
        {sidebar}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() =>
              setMobileOpen(false)
            }
            className="absolute inset-0 bg-black/45"
          />

          <aside className="relative h-full w-72">
            <button
              type="button"
              onClick={() =>
                setMobileOpen(false)
              }
              className="absolute right-4 top-4 z-10 rounded-full bg-white/10 p-2"
            >
              <X size={20} />
            </button>

            {sidebar}
          </aside>
        </div>
      )}

      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 flex h-20 items-center justify-between border-b border-black/5 bg-white/90 px-5 backdrop-blur sm:px-8">
          <button
            type="button"
            onClick={() =>
              setMobileOpen(true)
            }
            className="rounded-xl border border-black/10 p-2.5 lg:hidden"
          >
            <Menu size={21} />
          </button>

          <div className="hidden sm:block">
            <p className="text-sm text-[#746d65]">
              RiNi Bodycare
            </p>

            <p className="font-semibold text-[#38251e]">
              Administration
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#70845b] font-semibold text-white">
              {adminQuery.data?.name
                .charAt(0)
                .toUpperCase() ?? "R"}
            </div>

            <div className="hidden text-left sm:block">
              <p className="text-sm font-semibold text-[#38251e]">
                {adminQuery.data?.name}
              </p>

              <p className="text-xs text-[#746d65]">
                {adminQuery.data?.role}
              </p>
            </div>

            <ChevronDown
              size={16}
              className="text-[#746d65]"
            />
          </div>
        </header>

        <main className="p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
